import { Box, Divider as ChakraDivider, Stack } from '@chakra-ui/react';
import { usePaginationAdmin } from '@diamond/shared/context';
import { Breadcrumbs, Pagination } from '@diamond/shared/ui';
import { AdminLayout } from '@diamond/sol-admin/components';
import { useCleanSearchParams } from '@diamond/sol-admin/hooks';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  VoucherEmptyState,
  VoucherFilterBar,
  VoucherHeader,
  VoucherList,
  VoucherLoadingSkeleton,
  VoucherModalManager,
} from '../components';
import {
  VOUCHER_MECHANISM_OPTIONS,
  VOUCHER_PAGE_BREADCRUMBS,
  VOUCHER_STATUS_OPTIONS,
} from '../constants';
import {
  useVoucherActions,
  useVoucherFilters,
  useVoucherMutations,
  useVouchers,
} from '../hooks';
import type { VoucherListItem } from '../types';

// Types
type DateFilterForm = {
  'voucher_period-from': string;
  'voucher_period-to': string;
};

// Helper function to parse page from URL
const getCurrentPageFromUrl = (params: URLSearchParams): number => {
  return parseInt(params.get('page') || '1', 10);
};

// Helper function to find option by value
const findOptionByValue = <T extends { value: string }>(
  options: T[],
  value: string
): T | null => {
  return options.find((opt) => opt.value === value) || null;
};

export function VoucherPage() {
  const navigate = useNavigate();

  // URL-based pagination
  const { searchParams: urlParams, setParams } = useCleanSearchParams();
  const currentPage = getCurrentPageFromUrl(urlParams);

  const setCurrentPage = useCallback(
    (page: number) => {
      setParams({ page: page.toString() });
    },
    [setParams]
  );

  // Filters management
  const { filters, searchHook, selectParams, dateFilterHook } =
    useVoucherFilters(currentPage);

  // Date form management
  const methods = useForm<DateFilterForm>({
    defaultValues: {
      'voucher_period-from': dateFilterHook.dateParams['voucher_period-from'],
      'voucher_period-to': dateFilterHook.dateParams['voucher_period-to'],
    },
  });

  // Sync form with URL date params
  useEffect(() => {
    const currentFrom = methods.getValues('voucher_period-from');
    const currentTo = methods.getValues('voucher_period-to');
    const urlFrom = dateFilterHook.dateParams['voucher_period-from'];
    const urlTo = dateFilterHook.dateParams['voucher_period-to'];

    if (currentFrom === urlFrom && currentTo === urlTo) {
      return;
    }

    methods.reset(
      {
        'voucher_period-from': urlFrom,
        'voucher_period-to': urlTo,
      },
      {
        keepDirty: true,
        keepErrors: true,
      }
    );
  }, [dateFilterHook.dateParams, methods]);

  // Data fetching
  const {
    data: voucherResponse,
    isLoading,
    isFetching,
    refetch,
  } = useVouchers(filters);

  const vouchers: VoucherListItem[] = voucherResponse?.data || [];
  const pageCount = voucherResponse?.pagination?.pages || 1;
  const totalItems = voucherResponse?.pagination?.rows;

  // Pagination controls
  const { nextPage, previousPage, lastPage, firstPage } = usePaginationAdmin(
    currentPage,
    setCurrentPage,
    refetch,
    pageCount
  );

  // Mutations
  const {
    handleDelete,
    handleStatusToggle,
    handleDisplayToggle,
    deleteLoadingId,
    statusLoadingId,
    displayLoadingId,
    invalidateQueries,
  } = useVoucherMutations();

  // Actions
  const { handleIndexChange, handleMoveToTop } = useVoucherActions({
    invalidateQueries,
  });

  // Navigation handler
  const handleEdit = useCallback(
    (id: string) => {
      navigate(`/vouchers/detail/${id}`);
    },
    [navigate]
  );

  // Loading states
  const isTableLoading = isLoading || (isFetching && vouchers.length === 0);
  const showPagination = vouchers.length > 0 && pageCount > 1;

  if (isTableLoading) {
    return <VoucherLoadingSkeleton />;
  }

  return (
    <AdminLayout>
      <Stack spacing={6}>
        {/* Breadcrumbs */}
        <Box>
          <Breadcrumbs links={VOUCHER_PAGE_BREADCRUMBS} useRouterDom />
        </Box>

        <VoucherModalManager data={vouchers} onSuccess={invalidateQueries}>
          {({ onAdd, onDuplicate }) => (
            <>
              {/* Header */}
              <VoucherHeader title="Voucher Management" showAction />

              {/* Filters */}
              <Stack spacing={3}>
                <VoucherFilterBar
                  methods={methods}
                  searchRegister={searchHook.register}
                  handleSearch={searchHook.handleSearch}
                  handleDateFilter={dateFilterHook.handleDateFilter}
                  updateDateParams={dateFilterHook.updateDateParams}
                  rewardTypeValue={findOptionByValue(
                    VOUCHER_MECHANISM_OPTIONS,
                    selectParams.rewardType
                  )}
                  onRewardTypeChange={selectParams.updateRewardType}
                  statusVoucherValue={findOptionByValue(
                    VOUCHER_STATUS_OPTIONS,
                    selectParams.statusVoucher
                  )}
                  onStatusVoucherChange={selectParams.updateStatusVoucher}
                />
              </Stack>

              <ChakraDivider borderColor="gray.200" />

              {/* Content */}
              {vouchers.length === 0 ? (
                <VoucherEmptyState />
              ) : (
                <VoucherList
                  data={vouchers}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onDuplicate={onDuplicate}
                  onIndexChange={handleIndexChange}
                  onMoveToTop={handleMoveToTop}
                  onToggleStatus={handleStatusToggle}
                  onToggleDisplay={handleDisplayToggle}
                  deleteLoadingId={deleteLoadingId}
                  statusLoadingId={statusLoadingId}
                  displayLoadingId={displayLoadingId}
                  currentPage={currentPage}
                  totalItems={totalItems}
                />
              )}
            </>
          )}
        </VoucherModalManager>

        {/* Pagination */}
        {showPagination && (
          <Box display="flex" justifyContent="center">
            <Pagination
              currentPage={currentPage}
              totalPage={pageCount}
              nextPage={nextPage}
              previousPage={previousPage}
              firstPage={firstPage}
              lastPage={lastPage}
            />
          </Box>
        )}
      </Stack>
    </AdminLayout>
  );
}
